import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ApiClientError, Table, type Column } from 'grabbitbot-legacy-ui'
import { adminClient } from '../../api'
import type { AdminAuth0UnbindResult, AdminUserDetailed } from '../../api/adminTypes'
import { AdminNotice, ConfirmButton } from './adminShared'
import { describeAdminError, useAdminActions, type AdminFeedback } from './adminHelpers'
/**
 * Equivalent of legacy `AdminUserListComponent` (route `admin/users`,
 * contract #1/#2). `GET /admin/users/detailed` is the primary data source
 * (email + serviceId, needed for every cross-link below); the plain `GET
 * /admin/users` (#1) backs the row count and a quick text filter.
 *
 * Every row link carries `?email=` — the convention every other email-scoped
 * admin page reads via `useEmailParam()` (`adminShared.tsx`), so "view this
 * user's credit/bot status/payment record" is one click, matching how the
 * legacy admin nav worked.
 */

type UnbindOutcome = AdminAuth0UnbindResult | { notFound: true }

/** Plain wording for each unbind outcome. */
function describeUnbind(result: UnbindOutcome, email: string): AdminFeedback {
  if ('notFound' in result) return { tone: 'warning', text: `User not found: ${email}.` }
  if (!result.unbound) return { tone: 'warning', text: `Nothing to unbind for ${result.email}.` }
  const what =
    result.connection === 'google-oauth2'
      ? 'Google'
      : result.connection === 'auth0'
        ? 'Auth0 (password)'
        : 'Auth0'
  return {
    tone: 'success',
    text: `Unbound the ${what} login of ${result.email}. The next login links by verified e-mail.`,
  }
}

export function AdminUsersPage() {
  const { isBusy, feedbackFor, run, setFeedback: setRowFeedback } = useAdminActions()
  const [users, setUsers] = useState<AdminUserDetailed[] | null>(null)
  const [feedback, setFeedback] = useState<AdminFeedback | undefined>()
  const [filter, setFilter] = useState('')

  useEffect(() => {
    let cancelled = false
    adminClient
      .listUsersDetailed()
      .then((rows) => {
        if (!cancelled) setUsers(rows)
      })
      .catch((err) => {
        if (!cancelled) setFeedback(describeAdminError(err))
      })
    return () => {
      cancelled = true
    }
  }, [])

  const filtered = useMemo(() => {
    const list = users ?? []
    const needle = filter.trim().toLowerCase()
    return needle ? list.filter((u) => u.email.toLowerCase().includes(needle)) : list
  }, [users, filter])

  function unbind(email: string) {
    const key = `unbind:${email}`
    void run(
      key,
      // A 404 `user_not_found` is an expected outcome here, not a failure.
      () =>
        adminClient.unbindAuth0(email).catch((err: unknown): UnbindOutcome => {
          if (err instanceof ApiClientError && err.status === 404) return { notFound: true }
          throw err
        }),
      (result) => setRowFeedback(key, describeUnbind(result, email)),
    )
  }

  const columns: Column<AdminUserDetailed>[] = [
    { key: 'email', label: 'E-mail', align: 'left', sortValue: (r) => r.email },
    { key: 'serviceId', label: 'Service id', align: 'left', sortValue: (r) => r.serviceId ?? '' },
    {
      key: 'links',
      label: 'Admin views',
      sortable: false,
      format: (_v, r) => {
        const q = `?email=${encodeURIComponent(r.email)}`
        return (
          <span className="portal-actions">
            <Link to={`/admin/users/credit${q}`}>Credit</Link>
            <Link to={`/admin/bot/user/status${q}`}>Bot status</Link>
            <Link to={`/admin/payment/user${q}`}>Payment</Link>
            <Link to={`/admin/statistics/profit/lifetime${q}`}>Lifetime profit</Link>
            <Link to={`/admin/log/user${q}`}>Logs</Link>
          </span>
        )
      },
    },
    {
      key: 'unbind',
      label: 'Login link',
      sortable: false,
      align: 'left',
      format: (_v, r) => {
        const key = `unbind:${r.email}`
        const fb = feedbackFor(key)
        return (
          <span className="portal-actions">
            <ConfirmButton
              label="Unlink Auth0/Google login"
              pendingText={`Unlink the Auth0/Google login of ${r.email}?`}
              disabled={isBusy(key)}
              onConfirm={() => unbind(r.email)}
            />
            {fb && (
              <span role={fb.tone === 'error' ? 'alert' : 'status'} className={`portal-notice portal-notice--${fb.tone}`}>
                {fb.text}
              </span>
            )}
          </span>
        )
      },
    },
  ]

  return (
    <div>
      <div className="portal-page-title">
        <h1>Admin — all users</h1>
      </div>
      <div className="portal-card">
        <AdminNotice feedback={feedback} />
        <div className="portal-toolbar">
          <label htmlFor="admin-users-filter">Filter by e-mail</label>
          <input id="admin-users-filter" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="alice@" />
        </div>
        {users === null && !feedback && <p>Loading…</p>}
        {users !== null && (
          <>
            <h3>
              {filtered.length} of {users.length} users
            </h3>
            <div className="portal-table-scroll">
              <Table columns={columns} rows={filtered} rowKey={(r) => r.email} />
            </div>
          </>
        )}
      </div>
    </div>
  )
}
